import React, { useEffect, useState } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import './QuestionsDisplay.css';

// Math rendering component
const MathText = ({ text }) => {
    if (!text) return null;

    try {
        // Split text by math delimiters - improved regex to handle more cases
        const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[^$\n]+\$|\\\[[\s\S]*?\\\]|\\\(.*?\\\))/g);

        return (
            <>
                {parts.map((part, index) => {
                    if (part.startsWith('$$') && part.endsWith('$$')) {
                        // Block math
                        const math = part.slice(2, -2).trim();
                        if (math) {
                            try {
                                return <BlockMath key={index} math={math} />;
                            } catch (mathError) {
                                console.warn('Block math rendering failed:', mathError, 'Math:', math);
                                return <code key={index} style={{background: '#ffe6e6', padding: '2px 4px', borderRadius: '3px'}}>{part}</code>;
                            }
                        }
                    } else if ((part.startsWith('$') && part.endsWith('$') && part.length > 2) ||
                               (part.startsWith('\\(') && part.endsWith('\\)'))) {
                        // Inline math - handle both $...$ and \(...\)
                        let math = '';
                        if (part.startsWith('$') && part.endsWith('$')) {
                            math = part.slice(1, -1).trim();
                        } else if (part.startsWith('\\(') && part.endsWith('\\)')) {
                            math = part.slice(2, -2).trim();
                        }

                        if (math) {
                            try {
                                return <InlineMath key={index} math={math} />;
                            } catch (mathError) {
                                console.warn('Inline math rendering failed:', mathError, 'Math:', math);
                                return <code key={index} style={{background: '#ffe6e6', padding: '2px 4px', borderRadius: '3px'}}>{part}</code>;
                            }
                        }
                    } else if (part.startsWith('\\[') && part.endsWith('\\]')) {
                        // Display math \[...\]
                        const math = part.slice(2, -2).trim();
                        if (math) {
                            try {
                                return <BlockMath key={index} math={math} />;
                            } catch (mathError) {
                                console.warn('Display math rendering failed:', mathError, 'Math:', math);
                                return <code key={index} style={{background: '#ffe6e6', padding: '2px 4px', borderRadius: '3px'}}>{part}</code>;
                            }
                        }
                    }
                    // Regular text - only render if not empty
                    return part ? <span key={index}>{part}</span> : null;
                })}
            </>
        );
    } catch (error) {
        console.warn('Math rendering error:', error);
        // Fallback to plain text with better formatting
        return (
            <span style={{
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                padding: '2px 4px',
                borderRadius: '3px',
                border: '1px solid #ddd'
            }}>
                {text}
            </span>
        );
    }
};

const QuestionsDisplay = ({ 
    questions: initialQuestions = [], 
    error: externalError = "",
    onSelect
}) => {
    const [questions, setQuestions] = useState(initialQuestions);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(externalError);

    // Update local state when questions prop changes
    useEffect(() => {
        console.log('QuestionsDisplay: initialQuestions changed:', initialQuestions?.length || 0, 'items');
        const questionsOnly = initialQuestions.map(({ id, question }) => ({
            id,
            question
        }));
        setQuestions(questionsOnly);
        setLoading(false);
    }, [initialQuestions]);

    // Update error state when external error changes
    useEffect(() => {
        setError(externalError);
    }, [externalError]);

    return (
        <div className="qd-container">
            {loading ? (
                <div className="qd-loading">
                    <p>Loading questions...</p>
                </div>
            ) : error ? (
                <div className="qd-error">{error}</div>
            ) : questions && questions.length > 0 ? (
                <>
                    <div className="qd-main">   
                        {/* Questions Section */}
                        <div className="qd-list-wrapper">
                            <ul
                                className="qd-list"
                                onMouseLeave={() => onSelect && onSelect(-1)}
                            >
                                {questions.slice(0, 5).map((item, index) => (
                                    <li
                                        key={item.id || index}
                                        className="qd-item"
                                        onMouseEnter={() => onSelect && onSelect(index)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (!onSelect) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onSelect(index);
                                            }
                                        }}
                                    >
                                        <MathText text={item.question} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </>
            ) : (
                <div className="qd-empty qd-fade-in">
                    <div className="qd-status">
                        {error ? (
                            <p className="qd-error-text">{error}</p>
                        ) : (
                            <p>Please select a job to view its interview questions.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionsDisplay;
